/**
 * Node Template Service
 * Main service that orchestrates loading, enhancing, and providing access to NodeTemplates
 */

import { NodeTemplateApiClient, type NodeTemplateApiClientOptions } from '../ApiClient';
import { NodeTemplateEnhancer } from '../MapperServices';
import { emitEnhancementStarted, emitEnhancementCompleted, emitReady } from '../Events/NodeTemplateEvents';
import type { NodeType } from '../Domain/NodeType';
import type { NodeCategory } from '../Domain/NodeCategory';
import type { NodeTemplate } from '../Domain/NodeTemplate';

// Extend Window interface to include nodeTemplates
declare global {
  interface Window {
    nodeTemplates?: NodeTemplate[];
  }
}

export class NodeTemplateService {
  private apiClient: NodeTemplateApiClient;
  private enhancer: NodeTemplateEnhancer;
  private templates: NodeTemplate[] | null = null;
  private nodeTypes: NodeType[] | null = null;
  private categories: NodeCategory[] | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  constructor(clientOptions: NodeTemplateApiClientOptions = {}) {
    this.apiClient = new NodeTemplateApiClient(clientOptions);
    this.enhancer = new NodeTemplateEnhancer();
  }

  /**
   * Initialize the service by loading and enhancing all data
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      console.warn('NodeTemplateService already initialized');
      return;
    }
    // Prevent concurrent calls (e.g. React StrictMode double-invoke) from starting two fetches
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = this._doInitialize();
    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  private async _doInitialize(): Promise<void> {

    try {
      // Fetch all data in parallel
      const { nodeTypes, nodeCategories, templates } = await this.apiClient.fetchAll();

      // Store raw data
      this.nodeTypes = nodeTypes;
      this.categories = nodeCategories;

      // Enhance templates
      emitEnhancementStarted();
      this.templates = this.enhancer.enhance(templates, nodeTypes, nodeCategories);
      emitEnhancementCompleted(this.templates);

      // Expose templates globally
      window.nodeTemplates = this.templates;

      // Mark as initialized
      this.initialized = true;

      // Emit ready event
      emitReady({
        templates: this.templates,
        nodeTypes: this.nodeTypes,
        nodeCategories: this.categories,
      });

    } catch (error) {
      console.error('Failed to initialize NodeTemplateService:', error);
      throw error;
    }
  }

  /**
   * Get all enhanced templates
   */
  getTemplates(): NodeTemplate[] {
    if (!this.initialized || !this.templates) {
      throw new Error('NodeTemplateService not initialized. Call initialize() first.');
    }
    return this.templates;
  }

  /**
   * Get all node types
   */
  getNodeTypes(): NodeType[] {
    if (!this.initialized || !this.nodeTypes) {
      throw new Error('NodeTemplateService not initialized. Call initialize() first.');
    }
    return this.nodeTypes;
  }

  /**
   * Get all node categories
   */
  getCategories(): NodeCategory[] {
    if (!this.initialized || !this.categories) {
      throw new Error('NodeTemplateService not initialized. Call initialize() first.');
    }
    return this.categories;
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get template by code
   */
  getTemplateByCode(code: string): NodeTemplate | undefined {
    if (!this.initialized || !this.templates) {
      throw new Error('NodeTemplateService not initialized. Call initialize() first.');
    }
    return this.templates.find(t => t.code === code);
  }

  /**
   * Get templates by category
   */
  getTemplatesByCategory(categoryCode: string): NodeTemplate[] {
    if (!this.initialized || !this.templates) {
      throw new Error('NodeTemplateService not initialized. Call initialize() first.');
    }
    return this.templates.filter(t => t.category === categoryCode);
  }

  /**
   * Reset the service to allow re-initialization
   * Useful for refreshing templates after manual updates
   */
  reset(): void {
    this.templates = null;
    this.nodeTypes = null;
    this.categories = null;
    this.initialized = false;
    window.nodeTemplates = undefined;
  }

  /**
   * Refresh templates by resetting and re-initializing
   * Useful for hot-reloading templates during development
   */
  async refresh(): Promise<void> {
    console.log('Refreshing node templates...');
    this.reset();
    await this.initialize();
    console.log('Node templates refreshed successfully');
  }
}
